import { NodeGraph } from './NodeGraph';
import { BRIGHT_NODE, HERO_GRAPH, litNodesFor, type HeroVariant } from '@lib/heroNodes';

// Scrim tuned to the copy position: concentrated where the text sits so it stays
// legible, while the network lightens toward the corners and edges.
const SCRIM: Record<'left' | 'center', string> = {
  left: 'radial-gradient(115% 85% at 14% 52%, hsl(215 50% 12% / 0.82) 0%, hsl(215 50% 12% / 0.5) 38%, hsl(215 50% 12% / 0) 66%)',
  center:
    'radial-gradient(92% 92% at 50% 50%, hsl(215 50% 12% / 0.82) 0%, hsl(215 50% 12% / 0.52) 42%, hsl(215 50% 12% / 0) 80%)',
};

interface NodeGraphBackgroundProps {
  /** Where the copy sits, so the scrim protects it. Defaults to left-aligned. */
  focus?: 'left' | 'center';
  /**
   * Which HDR node to light. 'hero' suits a tall homepage hero; 'page' picks a
   * vertically-centred node that survives the shorter page-hero crop. Each has
   * its own pre-baked overlay in public/images.
   */
  variant?: HeroVariant;
  className?: string;
}

/**
 * The full hero background treatment built from the brand's node-network motif:
 * a crisp live graph with bright "live" nodes, teal glows emanating from the
 * bottom-right and top-left corners, and a scrim that keeps overlaid copy legible.
 * Sits behind content as an absolutely-positioned layer.
 */
export function NodeGraphBackground({
  focus = 'left',
  variant = 'hero',
  className = '',
}: NodeGraphBackgroundProps) {
  return (
    <div className={`node-graph-hdr absolute inset-0 overflow-hidden ${className}`.trim()}>
      <NodeGraph
        className="w-full h-full text-teal-light opacity-90"
        {...HERO_GRAPH}
        litNodes={litNodesFor(variant)}
        litColor="var(--lit-node)"
        brightNodes={[BRIGHT_NODE[variant]]}
        brightColor="var(--lit-node-bright)"
      />
      {/* Strong glow emanating from the bottom-right corner. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(100% 110% at 100% 100%, hsl(180 72% 47% / 0.4), hsl(180 72% 47% / 0.12) 48%, transparent 78%)',
          filter: 'blur(70px)',
        }}
      />
      {/* Scrim over the copy. */}
      <div className="absolute inset-0" style={{ background: SCRIM[focus] }} />
      {/* Smaller companion glow from the top-left, above the scrim so it reads. */}
      <div
        className="absolute inset-0 float-subtle"
        style={{
          background: 'radial-gradient(56% 62% at 0% 0%, hsl(190 72% 52% / 0.26), transparent 68%)',
          filter: 'blur(70px)',
          animationDelay: '-3s',
        }}
      />
      {/*
        True-HDR pass over the lit nodes. CSS colour can't exceed SDR white — an
        out-of-range color() clamps per channel to white — so the extra luminance
        comes from a PQ-encoded AVIF instead. It sits above the scrim (like the
        companion glow) because anything layered over it would just dim it back
        down, and `object-cover` on a 1600x900 image reproduces the SVG's
        xMidYMid slice exactly, so the glows stay registered to their nodes.
        CSS hides it entirely on SDR displays. Regenerate via
        `node scripts/generate-hdr-nodes.mjs` if the node indices change.
      */}
      <img
        src={`/images/${variant}-hdr-nodes.avif`}
        alt=""
        aria-hidden="true"
        decoding="async"
        className="hdr-node-overlay absolute inset-0 h-full w-full object-cover"
      />
    </div>
  );
}
